/**
 * Mock data sources for the EXCEL HR business pages.
 *
 * This file is a deliberately static, in-memory dataset. It backs all
 * 8 business pages (workbench / people / attendance / payroll /
 * recruit / approval / analytics / settings) and the gallery's
 * "Dashboard" sample.
 *
 * Design rules:
 *   - All names are clearly fictional (generic Chinese surnames and
 *     given names). They do not correspond to any real person at
 *     AxiomaticWorld or anywhere else.
 *   - Cross-record relationships are plausible but synthetic:
 *     `attendance[].employeeId` matches `employees[].id`,
 *     `payroll[].employeeId` matches `employees[].id`, etc.
 *   - Arrays are frozen at module load (`Object.freeze`) so a
 *     bug in a page that mutates a row produces a runtime exception
 *     instead of silently corrupting the dashboard for the rest of
 *     the session.
 *   - The mocked business week starts on a Monday seven days ago.
 *     `mockHelpers.ts` exposes the constants so tests can pin them.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EmployeeStatus = '在职' | '试用期' | '离职';

export type Employee = {
  id: string;
  name: string;
  department: string;
  title: string;
  status: EmployeeStatus;
  hireDate: string; // YYYY-MM-DD
};

export type AttendanceStatus = '正常' | '迟到' | '缺勤' | '请假';

export type AttendanceRecord = {
  id: string;
  employeeId: string;
  date: string; // YYYY-MM-DD
  checkIn: string; // HH:MM, '' if absent
  checkOut: string; // HH:MM, '' if absent
  status: AttendanceStatus;
};

export type PayrollRecord = {
  id: string;
  employeeId: string;
  month: string; // YYYY-MM
  base: number; // 基本工资
  bonus: number; // 奖金
  deduction: number; // 扣除
  net: number; // 实发(派生)
};

export type RecruitmentStatus = '招聘中' | '面试中' | '已发offer' | '已关闭';

export type JobPosting = {
  id: string;
  title: string;
  department: string;
  headcount: number; // 计划招聘人数
  applied: number; // 收到简历
  interviewed: number; // 已面试
  offered: number; // 已发 offer
  status: RecruitmentStatus;
};

export type ApprovalType = '请假' | '报销' | '加班' | '出差';

export type ApprovalStatus = '待审批' | '已通过' | '已驳回';

export type Approval = {
  id: string;
  type: ApprovalType;
  applicantId: string;
  applicantName: string;
  amount?: number; // 报销金额,仅 type === '报销'
  days?: number; // 请假 / 加班 / 出差天数
  status: ApprovalStatus;
  submittedAt: string; // YYYY-MM-DD HH:MM
};

export type Department = {
  id: string;
  name: string;
  headcount: number;
  manager: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The mocked business week is anchored to a fixed seven-day window
 * so that any `attendance` record looks stable across days. The page
 * simply derives "this week" from `MOCK_TODAY`. Tests assert against
 * these literals.
 */
export const MOCK_TODAY = '2026-07-10';

export const MOCK_WEEK_DATES: ReadonlyArray<string> = [
  '2026-07-06', // 周一
  '2026-07-07',
  '2026-07-08',
  '2026-07-09',
  '2026-07-10',
  '2026-07-11', // 周六
  '2026-07-12'  // 周日
];

export const MOCK_WEEK_LABELS: ReadonlyArray<string> = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

export const MOCK_DEPARTMENTS: ReadonlyArray<Department> = Object.freeze([
  { id: 'd-product',   name: '产品部',     headcount: 3, manager: '王伟' },
  { id: 'd-design',    name: '设计部',     headcount: 3, manager: '李芳' },
  { id: 'd-engineering', name: '工程部',   headcount: 4, manager: '张明' },
  { id: 'd-operations',  name: '运营部',   headcount: 3, manager: '陈静' },
  { id: 'd-marketing',   name: '市场部',   headcount: 3, manager: '刘洋' },
  { id: 'd-hr',          name: '人力资源部', headcount: 2, manager: '周娜' }
]);

// ---------------------------------------------------------------------------
// employees
// ---------------------------------------------------------------------------

export const employees: ReadonlyArray<Employee> = Object.freeze([
  { id: 'e-001', name: '王伟',   department: '产品部',     title: '产品总监',   status: '在职',   hireDate: '2021-03-15' },
  { id: 'e-002', name: '李芳',   department: '设计部',     title: '设计经理',   status: '在职',   hireDate: '2020-08-01' },
  { id: 'e-003', name: '张明',   department: '工程部',     title: '工程总监',   status: '在职',   hireDate: '2019-05-12' },
  { id: 'e-004', name: '陈静',   department: '运营部',     title: '运营经理',   status: '在职',   hireDate: '2022-01-04' },
  { id: 'e-005', name: '刘洋',   department: '市场部',     title: '市场总监',   status: '在职',   hireDate: '2020-11-20' },
  { id: 'e-006', name: '周娜',   department: '人力资源部', title: 'HR 经理',    status: '在职',   hireDate: '2021-06-09' },
  { id: 'e-007', name: '吴磊',   department: '产品部',     title: '产品经理',   status: '在职',   hireDate: '2023-02-14' },
  { id: 'e-008', name: '郑雅',   department: '设计部',     title: '高级设计师', status: '在职',   hireDate: '2022-09-01' },
  { id: 'e-009', name: '孙浩',   department: '工程部',     title: '高级工程师', status: '在职',   hireDate: '2021-07-22' },
  { id: 'e-010', name: '钱璐',   department: '工程部',     title: '工程师',     status: '试用期', hireDate: '2026-05-04' },
  { id: 'e-011', name: '李欣',   department: '运营部',     title: '运营专员',   status: '在职',   hireDate: '2023-11-15' },
  { id: 'e-012', name: '徐凯',   department: '市场部',     title: '市场专员',   status: '在职',   hireDate: '2024-03-08' },
  { id: 'e-013', name: '韩冰',   department: '设计部',     title: '设计师',     status: '在职',   hireDate: '2024-08-19' },
  { id: 'e-014', name: '高远',   department: '产品部',     title: '产品助理',   status: '试用期', hireDate: '2026-06-12' },
  { id: 'e-015', name: '冯悦',   department: '工程部',     title: '前端工程师', status: '在职',   hireDate: '2023-05-30' },
  { id: 'e-016', name: '陈颖',   department: '人力资源部', title: 'HR 专员',    status: '在职',   hireDate: '2024-01-15' },
  { id: 'e-017', name: '董涛',   department: '市场部',     title: '内容运营',   status: '在职',   hireDate: '2022-12-01' },
  { id: 'e-018', name: '曹雪',   department: '设计部',     title: '插画师',     status: '离职',   hireDate: '2020-04-01' }
]);

// ---------------------------------------------------------------------------
// attendance — 21 records (3 representative employees x 7 days)
// ---------------------------------------------------------------------------

function attendanceFromWeek(): AttendanceRecord[] {
  const out: AttendanceRecord[] = [];
  const sample = ['e-001', 'e-007', 'e-009'];
  const schedule: ReadonlyArray<readonly [string, string]> = [
    ['09:02', '18:35'],
    ['08:55', '18:40'],
    ['09:15', '18:25'], // 迟到一次
    ['09:00', '19:00'],
    ['08:50', '18:30'],
    ['', ''], // 缺勤
    ['', '']
  ];
  MOCK_WEEK_DATES.forEach((date, dayIdx) => {
    sample.forEach((employeeId, empIdx) => {
      const combo = schedule[dayIdx]!;
      const [checkIn, checkOut] = combo;
      let status: AttendanceStatus;
      if (!checkIn && !checkOut) status = '缺勤';
      else if (checkIn > '09:10') status = '迟到';
      else if (dayIdx === 4 && empIdx === 2) status = '请假'; // 周五请假一次
      else status = '正常';
      out.push({
        id: `a-${dayIdx}-${empIdx}`,
        employeeId,
        date,
        checkIn,
        checkOut,
        status
      });
    });
  });
  return out;
}

export const attendance: ReadonlyArray<AttendanceRecord> = Object.freeze(attendanceFromWeek());

// ---------------------------------------------------------------------------
// payroll — 12 records (each employee gets 1 month of payroll)
// ---------------------------------------------------------------------------

function payrollFromEmployees(): PayrollRecord[] {
  const month = '2026-06';
  return employees.map((emp, idx) => {
    const base = 8000 + idx * 600; // 8000-17800 之间
    const bonus = idx % 3 === 0 ? 1500 : idx % 4 === 0 ? 800 : 0;
    const deduction = 1200 + (idx % 5) * 100; // 1200-1600
    const net = base + bonus - deduction;
    return {
      id: `p-${emp.id}`,
      employeeId: emp.id,
      month,
      base,
      bonus,
      deduction,
      net
    };
  });
}

export const payroll: ReadonlyArray<PayrollRecord> = Object.freeze(payrollFromEmployees());

// ---------------------------------------------------------------------------
// recruitments — 8 records
// ---------------------------------------------------------------------------

export const recruitments: ReadonlyArray<JobPosting> = Object.freeze([
  { id: 'r-001', title: '高级前端工程师',   department: '工程部',     headcount: 2, applied: 38, interviewed: 8, offered: 1, status: '面试中' },
  { id: 'r-002', title: '产品经理',         department: '产品部',     headcount: 1, applied: 24, interviewed: 4, offered: 1, status: '已发offer' },
  { id: 'r-003', title: '高级设计师',       department: '设计部',     headcount: 1, applied: 30, interviewed: 6, offered: 0, status: '面试中' },
  { id: 'r-004', title: '数据分析师',       department: '运营部',     headcount: 1, applied: 18, interviewed: 3, offered: 0, status: '招聘中' },
  { id: 'r-005', title: '市场专员',         department: '市场部',     headcount: 2, applied: 22, interviewed: 4, offered: 2, status: '已关闭' },
  { id: 'r-006', title: '后端工程师',       department: '工程部',     headcount: 2, applied: 41, interviewed: 7, offered: 0, status: '面试中' },
  { id: 'r-007', title: 'HR 实习生',        department: '人力资源部', headcount: 2, applied: 56, interviewed: 10, offered: 3, status: '已发offer' },
  { id: 'r-008', title: '财务专员',         department: '运营部',     headcount: 1, applied: 14, interviewed: 2, offered: 0, status: '招聘中' }
]);

// ---------------------------------------------------------------------------
// approvals — 10 records (3 待审批 / 4 已通过 / 3 已驳回)
// ---------------------------------------------------------------------------

export const approvals: ReadonlyArray<Approval> = Object.freeze([
  { id: 'ap-001', type: '请假', applicantId: 'e-007', applicantName: '吴磊', days: 2, status: '待审批', submittedAt: '2026-07-09 09:14' },
  { id: 'ap-002', type: '报销', applicantId: 'e-009', applicantName: '孙浩', amount: 1280, status: '待审批', submittedAt: '2026-07-09 11:42' },
  { id: 'ap-003', type: '加班', applicantId: 'e-001', applicantName: '王伟', days: 1, status: '待审批', submittedAt: '2026-07-10 08:05' },
  { id: 'ap-004', type: '出差', applicantId: 'e-005', applicantName: '刘洋', days: 4, status: '已通过', submittedAt: '2026-07-07 14:30' },
  { id: 'ap-005', type: '报销', applicantId: 'e-002', applicantName: '李芳', amount: 480, status: '已通过', submittedAt: '2026-07-06 10:11' },
  { id: 'ap-006', type: '请假', applicantId: 'e-013', applicantName: '韩冰', days: 1, status: '已通过', submittedAt: '2026-07-08 16:55' },
  { id: 'ap-007', type: '加班', applicantId: 'e-008', applicantName: '郑雅', days: 2, status: '已通过', submittedAt: '2026-07-05 09:20' },
  { id: 'ap-008', type: '报销', applicantId: 'e-012', applicantName: '徐凯', amount: 320, status: '已驳回', submittedAt: '2026-07-04 17:08' },
  { id: 'ap-009', type: '请假', applicantId: 'e-014', applicantName: '高远', days: 5, status: '已驳回', submittedAt: '2026-07-03 13:33' },
  { id: 'ap-010', type: '出差', applicantId: 'e-011', applicantName: '李欣', days: 3, status: '已驳回', submittedAt: '2026-07-02 11:00' }
]);

// ---------------------------------------------------------------------------
// System / org settings (used by Settings page)
// ---------------------------------------------------------------------------

export const orgProfile: Readonly<{
  companyName: string;
  shortName: string;
  logoMark: string;
  domain: string;
  foundedYear: number;
}> = Object.freeze({
  companyName: '示例智能科技(深圳)有限公司',
  shortName: 'EXCEL HR',
  logoMark: '◆',
  domain: 'excelhr.example.com',
  foundedYear: 2018
});

export const roles: ReadonlyArray<{
  id: string;
  label: string;
  description: string;
  permissions: ReadonlyArray<string>;
}> = Object.freeze([
  {
    id: 'role-admin',
    label: '管理员',
    description: '拥有全部模块的读写权限,负责系统配置与最终审批。',
    permissions: ['员工管理', '考勤管理', '薪酬管理', '招聘管理', '审批管理', '数据分析', '系统设置']
  },
  {
    id: 'role-hr',
    label: 'HR',
    description: '负责员工档案、考勤审批与招聘流程。',
    permissions: ['员工管理', '考勤管理', '招聘管理', '审批管理', '数据分析']
  },
  {
    id: 'role-employee',
    label: '员工',
    description: '查看个人档案、提交请假/报销、查看公开统计。',
    permissions: ['个人信息', '提交审批', '查看团队']
  }
]);